<template>
  <b-container fluid>
    <b-row align-v="center" :class="{ header: true, 'is-app': isApp }">
      <b-col class="seatNumber addBorder" cols="2">
        <div>
          <div class="seatNumber-title">{{ $t('common.seatNumber') }}</div>
          <div class="seatNumber-number">
            {{ $t('common.numberBefore') }}
            <span>{{ seatNo }}</span>
            {{ $t('common.numberAfter') }}
          </div>
        </div>
      </b-col>
      <b-col class="orderNumber addBorder" cols="2">
        <div :class="{ 'is-us': isUs }">
          <div class="orderNumber-title">{{ $t('common.finishedDishes') }}</div>
          <div class="orderNumber-number">
            <span>{{ dishCount }}</span>
            <template v-if="!isUs">{{ $t('common.dishes') }}</template>
          </div>
        </div>
      </b-col>
      <b-col class="buttonsArea addBorder" cols="6">
        <div class="buttonsArea-button" @click="handleCallStaff">
          <div class="buttonsArea-button_callButton">{{ $t('common.call') }}</div>
        </div>
        <div class="buttonsArea-button" @click="handleCheckout">
          <div class="buttonsArea-button_checkButton">{{ $t('common.check') }}</div>
        </div>
      </b-col>
      <b-col class="hamburger" cols="2">
        <div @click="openSideMenu">
          <div class="HamburgerMenu"></div>
        </div>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import * as api from '@/api'

export default {
  computed: {
    ...mapGetters({
      languageId: 'setting/getLanguage',
      shopId: 'seat/getShopId',
      seatNo: 'seat/getSeatNo',
      dishCount: 'seat/getDishCount',
      isSelfcheckEnabled: 'seat/getIsSelfcheckEnabled',
      isTable: 'selfcheck/getIsTable',
      isSelfcheckReady: 'selfcheck/getIsSelfcheckReady',
      selfcheckError: 'selfcheck/getError',
    }),
    isApp() {
      return process.env.VUE_APP_TYPE === 'app'
    },
    defaultLanguage() {
      return process.env.VUE_APP_LANG
    },
    isUs() {
      return this.defaultLanguage === 'us'
    },
  },
  watch: {
    languageId(newLanguage) {
      this.$i18n.locale = newLanguage
    },
  },
  methods: {
    ...mapActions({
      dispatchInitSelfcheck: 'selfcheck/initSelfcheck',
      setShowingSelfcheckInitErrorModal: 'modal/setShowingSelfcheckInitErrorModal',
      setShowingCheckingOutModal: 'modal/setShowingCheckingOutModal',
      setShowingCallingStaffModal: 'modal/setShowingCallingStaffModal',
      setShowingSimpleErrorModal: 'modal/setShowingSimpleErrorModal',
    }),
    openSideMenu() {
      this.$emit('openSideMenu')
    },
    async initSelfcheck() {
      await this.dispatchInitSelfcheck()

      if (!this.selfcheckError) {
        if (this.isSelfcheckReady) {
          this.navigateToSelfcheck()
        } else {
          this.setShowingCheckingOutModal(true)
        }
      } else {
        if (this.selfcheckError.response) {
          // server error
          this.setShowingSelfcheckInitErrorModal(true)
        } else {
          // network error
          this.setShowingSimpleErrorModal(true)
        }
      }
    },
    async checkout() {
      try {
        await api.checkout(this.shopId, this.seatNo)
        this.setShowingCheckingOutModal(true)
      } catch (error) {
        this.setShowingSimpleErrorModal(true)
      }
    },
    async handleCheckout() {
      if (this.isSelfcheckEnabled) {
        this.initSelfcheck()
      } else {
        this.checkout()
      }
    },
    async handleCallStaff() {
      try {
        await api.callStaff(this.shopId, this.seatNo)
        this.setShowingCallingStaffModal(true)
      } catch (error) {
        this.setShowingSimpleErrorModal(true)
      }
    },
    navigateToSelfcheck() {
      if (this.isTable) {
        this.$router.push({ name: 'dishesConfirm' })
      } else {
        this.$router.push({ name: 'totalAmount' })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.header {
  > div {
    height: 55px;
    display: flex;
    justify-content: center;
    align-items: center;
    color: #242424;

    .is-app & {
      color: #fff;
    }
  }

  .seatNumber {
    padding: 0;
    line-height: 1;
    > div {
      margin-bottom: -4px;
    }
    &-title {
      display: block;
      font-size: 10px;
      min-height: 18px;
    }
    &-number {
      display: block;
      font-size: 10px;
      line-height: 1;
      span {
        font-size: 18px;
        font-weight: bold;
      }
    }
  }
  .orderNumber {
    padding: 0;
    line-height: 1;
    > div {
      margin-bottom: -4px;
      &.is-us {
        margin-bottom: 0;
      }
    }
    &-title {
      display: block;
      font-size: 10px;
      margin-bottom: 2px;
      min-height: 18px;
    }
    &-number {
      display: block;
      font-size: 10px;
      line-height: 1;
      span {
        font-size: 18px;
        font-weight: bold;
      }
    }
  }
  .buttonsArea {
    @media (max-width: 576px) {
      padding-left: 0;
      padding-right: 0;
    }
    &-button {
      width: 50%;
      padding: 0 5px;
      box-sizing: border-box;
      font-weight: bold;
      > div {
        width: 100%;
        height: 38px;
        line-height: 38px;
        border-radius: 5px;
      }
      &_callButton {
        color: #222;
        background-color: #e6e6e6;
        border-bottom: 2px solid #bfbfbf;
        font-size: 14px;
        // for iphone5/SE
        @media screen and (max-width: 375px) {
          font-size: 0.8rem;
        }
        &:active {
          border-top: 2px solid #242424;
          border-bottom: 0px solid #bfbfbf;
        }
      }
      &_checkButton {
        height: 38px;
        color: #fff;
        background-color: #d21b24;
        border-bottom: 2px solid #96131a;
        font-size: 14px;
        // for iphone5/SE
        @media screen and (max-width: 375px) {
          font-size: 0.8rem;
        }
        &:active {
          border-top: 2px solid #242424;
          border-bottom: 0px solid #ad1219;
        }
      }
    }
  }
}
.hamburger {
  margin: 0;
  padding: 0;
  div {
    height: 35px;
    width: 100%;
    margin: 0 auto;
    @media (max-width: 576px) {
      padding: 0;
    }
    .HamburgerMenu {
      margin-top: 17px;
      position: relative;
      display: block;
      content: 'aa';
      height: 2px;
      width: 25px;
      background: #242424;

      .is-app & {
        background: #fff;
      }
    }
    .HamburgerMenu::before {
      content: '';
      display: block;
      position: absolute;
      top: 7px;
      left: 0;
      width: 25px;
      height: 2px;
      background: #242424;

      .is-app & {
        background: #fff;
      }
    }
    .HamburgerMenu::after {
      content: '';
      display: block;
      position: absolute;
      height: 2px;
      width: 25px;
      background: #242424;
      top: -7px;
      left: 0;

      .is-app & {
        background: #fff;
      }
    }
  }
}
.addBorder::after {
  position: absolute;
  content: '';
  top: 12;
  right: 0;
  height: 30px;
  width: 1px;
  background: #ccc;

  .is-app & {
    background: #414141;
  }
}
</style>
